/* =====================================================================
 * DMMessageBubble — DM 메시지 말풍선 (클릭 기반 액션바 + 이모지 리액션)
 * ===================================================================== */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import { Smile, Copy, Check } from 'lucide-react';
import type { DMMessage, ReactionEmoji } from '@auw/workspace-utils/types/dm';
import { getAgentInfo } from '@auw/workspace-utils/types/dm';
import { useDMStore } from './hooks/useDMStore';
import { useUserStore } from '@auw/stores';
import { sanitizeHtml } from '@auw/workspace-utils';
import DMRichCard from './DMRichCard';
import DMEmojiPicker from './DMEmojiPicker';
import DMInvitationCard from './DMInvitationCard';

interface Props {
  message: DMMessage;
}

const DMMessageBubble: React.FC<Props> = ({ message }) => {
  const { t } = useTranslation('common');
  const isUser = message.direction === 'user_to_agent';
  const agent = getAgentInfo(message.agent_type);
  const isSystemApp = message.agent_type === 'system_app';
  const toggleReaction = useDMStore((s) => s.toggleReaction);
  const currentUserId = useUserStore((s) => s.user?.userId || s.user?.email || '');

  const timeStr = formatTime(message.created_at);
  const isCard = message.content_type === 'card' && message.metadata?.card_data;
  const isInvitation = message.content_type === 'invitation' && message.metadata?.invitation_id;
  const reactions = message.reactions || [];

  const [showActions, setShowActions] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [copied, setCopied] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const emojiTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setAvatarError(false);
  }, [message.agent_type]);

  // 바깥 클릭 시 액션바 닫기
  useEffect(() => {
    if (!showActions) return;
    const handler = (e: MouseEvent) => {
      if (showPicker) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowActions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showActions, showPicker]);

  const handleBubbleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (showPicker) return;
    setShowActions((v) => !v);
  }, [showPicker]);

  const handleEmojiClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPicker(true);
  }, []);

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      toggleReaction(message.agent_type, message.id, emoji as ReactionEmoji, currentUserId);
      setShowPicker(false);
      setShowActions(false);
    },
    [toggleReaction, message.agent_type, message.id, currentUserId],
  );

  const handlePickerClose = useCallback(() => {
    setShowPicker(false);
  }, []);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowActions(false);
      }, 1200);
    } catch {
      // fallback
    }
  }, [message.content]);

  const handleExistingReactionClick = useCallback(
    (emoji: ReactionEmoji) => {
      toggleReaction(message.agent_type, message.id, emoji, currentUserId);
    },
    [toggleReaction, message.agent_type, message.id, currentUserId],
  );

  const getAnchorRect = useCallback((): DOMRect | null => {
    return emojiTriggerRef.current?.getBoundingClientRect() ?? null;
  }, []);

  return (
    <Row $isUser={isUser}>
      {!isUser && (
        <MsgAvatar>
          {isSystemApp && !avatarError ? (
            <MsgAvatarImg
              src="/agent_logo.png"
              alt={agent.name}
              onError={() => setAvatarError(true)}
            />
          ) : (
            agent.letter
          )}
        </MsgAvatar>
      )}
      <BubbleWrap ref={wrapRef} $isUser={isUser}>
        {/* 말풍선 */}
        {isInvitation ? (
          <DMInvitationCard
            invitationId={message.metadata!.invitation_id!}
            invitationStatus={message.metadata!.invitation_status || 'pending'}
            teamName={message.metadata!.team_name || ''}
            inviterName={message.metadata!.inviter_name}
            isInvitee={!isUser}
          />
        ) : (
          <BubbleClickArea onClick={handleBubbleClick}>
            {isCard ? (
              <DMRichCard metadata={message.metadata!} />
            ) : isUser ? (
              <UserBubble>{message.content}</UserBubble>
            ) : (
              <AgentBubble>
                <BubbleContent
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(formatContent(message.content, message.content_type)),
                  }}
                />
              </AgentBubble>
            )}
          </BubbleClickArea>
        )}

        {/* 리액션 배지 */}
        {reactions.length > 0 && (
          <ReactionBar $isUser={isUser}>
            {reactions.map((r) => (
              <ReactionBadge
                key={r.emoji}
                $active={r.userIds.includes(currentUserId)}
                onClick={() => handleExistingReactionClick(r.emoji)}
              >
                <span>{r.emoji}</span>
                {r.userIds.length > 1 && <ReactionCount>{r.userIds.length}</ReactionCount>}
              </ReactionBadge>
            ))}
          </ReactionBar>
        )}

        {/* 시간 + 읽음 */}
        <Meta $isUser={isUser}>
          <TimeText>{timeStr}</TimeText>
          {!isUser && message.read_at && <ReadStatus>{t('dm.read')}</ReadStatus>}
        </Meta>

        {/* 액션바 */}
        {showActions && (
          <ActionBarWrap $isUser={isUser}>
            <ActionBar>
              <ActionBtn
                ref={emojiTriggerRef}
                onClick={handleEmojiClick}
                title={t('dm.emojiReaction')}
              >
                <Smile size={13} />
              </ActionBtn>
              <ActionDivider />
              <ActionBtn onClick={handleCopy} title={t('dm.copyMessage')}>
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </ActionBtn>
            </ActionBar>
          </ActionBarWrap>
        )}

        {/* 이모지 피커 */}
        {showPicker && (
          <DMEmojiPicker
            onSelect={handleEmojiSelect}
            onClose={handlePickerClose}
            anchorRect={getAnchorRect()}
            isUser={isUser}
          />
        )}
      </BubbleWrap>
    </Row>
  );
};

export default DMMessageBubble;

/* ---- Helpers ---- */

function formatTime(isoStr: string): string {
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
}

function formatContent(content: string, contentType: string): string {
  if (contentType === 'markdown') {
    return content
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
  }
  return content.replace(/\n/g, '<br/>');
}

/* ---- Styled ---- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(3px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Row = styled.div<{ $isUser: boolean }>`
  display: flex;
  gap: 8px;
  max-width: ${({ theme }) => theme.dm.bubbleMaxWidth};
  animation: ${fadeIn} 0.2s ease;

  ${({ $isUser }) =>
    $isUser
      ? css`
          align-self: flex-end;
          flex-direction: row-reverse;
        `
      : css`
          align-self: flex-start;
        `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: ${({ theme }) => theme.dmMobile.bubbleMaxWidth};
  }
`;

const MsgAvatar = styled.div`
  flex-shrink: 0;
  width: ${({ theme }) => theme.dm.msgAvatarSize};
  height: ${({ theme }) => theme.dm.msgAvatarSize};
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  background: ${({ theme }) => theme.mode === 'dark' ? 'linear-gradient(145deg, #434345, #38383a)' : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) => theme.mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  font-weight: 700;
  font-size: ${({ theme }) => theme.dm.msgAvatarFontSize};
  line-height: 1;
  user-select: none;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.dmMobile.msgAvatarSize};
    height: ${({ theme }) => theme.dmMobile.msgAvatarSize};
    font-size: ${({ theme }) => theme.dmMobile.msgAvatarFontSize};
  }
`;

const MsgAvatarImg = styled.img`
  width: 76%;
  height: 76%;
  object-fit: contain;
  display: block;
`;

const BubbleWrap = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`;

const BubbleClickArea = styled.div`
  cursor: pointer;
`;

const UserBubble = styled.div`
  padding: ${({ theme }) => theme.dm.bubblePad};
  border-radius: ${({ theme }) =>
    `${theme.dm.bubbleRadius} ${theme.dm.bubbleRadius} ${theme.dm.bubbleRadiusTail} ${theme.dm.bubbleRadius}`};
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.textInverse};
  font-size: ${({ theme }) => theme.dm.bubbleFontSize};
  line-height: ${({ theme }) => theme.dm.bubbleLineHeight};
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.dmMobile.bubblePad};
    font-size: ${({ theme }) => theme.dmMobile.bubbleFontSize};
    line-height: ${({ theme }) => theme.dmMobile.bubbleLineHeight};
    border-radius: ${({ theme }) =>
      `${theme.dmMobile.bubbleRadius} ${theme.dmMobile.bubbleRadius} ${theme.dmMobile.bubbleRadiusTail} ${theme.dmMobile.bubbleRadius}`};
  }
`;

const AgentBubble = styled.div`
  padding: ${({ theme }) => theme.dm.bubblePad};
  border-radius: ${({ theme }) =>
    `${theme.dm.bubbleRadiusTail} ${theme.dm.bubbleRadius} ${theme.dm.bubbleRadius} ${theme.dm.bubbleRadius}`};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.dm.bubbleFontSize};
  line-height: ${({ theme }) => theme.dm.bubbleLineHeight};
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.dmMobile.bubblePad};
    font-size: ${({ theme }) => theme.dmMobile.bubbleFontSize};
    line-height: ${({ theme }) => theme.dmMobile.bubbleLineHeight};
    border-radius: ${({ theme }) =>
      `${theme.dmMobile.bubbleRadiusTail} ${theme.dmMobile.bubbleRadius} ${theme.dmMobile.bubbleRadius} ${theme.dmMobile.bubbleRadius}`};
  }
`;

const BubbleContent = styled.div`
  strong { font-weight: 600; }
  em { font-style: italic; }
`;

const Meta = styled.div<{ $isUser: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
`;

const TimeText = styled.span`
  font-size: ${({ theme }) => theme.dm.metaFontSize};
  color: ${({ theme }) => theme.colors.textMuted};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.dmMobile.metaFontSize};
  }
`;

const ReadStatus = styled.span`
  font-size: ${({ theme }) => theme.dm.metaFontSize};
  color: ${({ theme }) => theme.colors.success};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.dmMobile.metaFontSize};
  }
`;

/* -- 액션바 -- */

const ActionBarWrap = styled.div<{ $isUser: boolean }>`
  display: flex;
  padding: 2px 0;
  animation: ${slideUp} 0.12s ease;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`;

const ActionBar = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  overflow: hidden;
`;

const ActionBtn = styled.button`
  width: 26px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:active { transform: scale(0.92); }
`;

const ActionDivider = styled.div`
  width: 1px;
  height: 14px;
  background: ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
`;

/* -- 리액션 배지 -- */

const ReactionBar = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 3px 0 1px;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`;

const ReactionBadge = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 10px;
  border: none;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(99,102,241,0.18)'
        : 'rgba(99,102,241,0.10)'
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.04)'};
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(99,102,241,0.22)'
        : 'rgba(99,102,241,0.12)'};
  }
`;

const ReactionCount = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 500;
`;
